import axios from "axios";
import { useCallback, useEffect, useState } from "react";

// Single axios instance: cookies + Bearer fallback
const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cms_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Authenticated file URL (cookies are sent on normal navigation)
export const apiFileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api${path}`;

// Resolve a media URL: relative `/api/...` paths get prefixed with the backend
// origin; absolute http(s) URLs and `/images/*` (bundled assets) pass through.
export const mediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  return path;
};

export default api;

// ---- formatting helpers ----
export const fmtRM = (n) =>
  `RM ${Number(n || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtPct = (r) => `${Math.round(Number(r || 0) * 10000) / 100}%`;

export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const dayName = (d) => DAYS[d] ?? "";

export const MONTH_NOW = new Date().toISOString().slice(0, 7);

// FastAPI error detail can be a string, an array of {msg}, or an object with conflicts
export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join("; ");
  if (d.conflicts) return [d.message, ...d.conflicts].join("\n");
  if (typeof d.message === "string") return d.message;
  if (typeof d.msg === "string") return d.msg;
  return JSON.stringify(d);
}

// Tiny fetch hook used across pages
export function useList(endpoint, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    if (!endpoint) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(endpoint)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, ...deps]);
  return { data, loading, reload };
}
